import { ReactNode, useEffect, useState, type CSSProperties } from 'react';
import { Navbar, Nav, Container, NavDropdown, Dropdown } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, isAdminUser } from '../../hooks/useAuth';
import LanguageSwitcher from '../common/LanguageSwitcher';
import Footer from './Footer';
import { formatUserName } from '../../utils/name.util';
import { SUPPORTED_LOCALES } from '../../i18n/config';
import { isPublicDemoAccessPath, isTppPublicPathname } from '../../utils/publicAccessPaths';
import { isKeisukePublicEnabled, isLaboSukePublicEnabled, isTppPublicEnabled } from '../../utils/config';
import { keisukePublicPath, isKeisukeWorkspacePathname } from '../../utils/keisukePath';
import { laboSukePublicPath } from '../../utils/laboSukePath';
import { isKeibaArenaPublicPath, keibaArenaPublicPath } from '../../utils/keibaArenaPath';
import { banquetPublicPath } from '../../utils/banquetPath';
import { econReportPublicPath } from '../../utils/econReportPath';
import { gaisukeFxPublicPath } from '../../utils/gaisukeFxPath';
import {
  getRsShareBoardTitle,
  subscribeRsShareBoardTitle,
} from '../../utils/reservationBoardSharePath.util';
import {
  isSampleManagerPathname,
  SAMPLE_MANAGER_NAV_ITEMS,
  SampleManagerSectionSelect,
} from '../samples/SampleManagerNavigation';
import { recordFeatureEntrance } from '../../services/featureEntrance.service';
import type { KensukeFeatureCardId } from '../../constants/kensukeFeatureCards';
import './LayoutNav.css';

interface LayoutProps {
  children: ReactNode;
}

const trackFeatureEntrance = (id: KensukeFeatureCardId) => () => recordFeatureEntrance(id);

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isGuestOnPublicDemo = !user && isPublicDemoAccessPath(location.pathname);
  const laboSukeNavVisible = isLaboSukePublicEnabled() || isAdminUser(user);
  const tppNavVisible = isTppPublicEnabled() || Boolean(user);
  const keisukeNavVisible = isKeisukePublicEnabled() || !!user;
  const [shareBoardTitle, setShareBoardTitle] = useState(() => getRsShareBoardTitle());
  /** ハンバーガー展開状態（遷移後は閉じる） */
  const [navExpanded, setNavExpanded] = useState(false);

  // ページ遷移時にスクロール位置をトップにリセットし、折りたたみナビを閉じる
  useEffect(() => {
    window.scrollTo(0, 0);
    setNavExpanded(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /** 計助 V1 / 競馬アリーナ / 遺伝子解析: ナビ＋フッターを除いた残りを main に割り当て */
  const p = location.pathname;
  const keisukeFlexViewport = isKeisukeWorkspacePathname(p);
  const keibaArenaViewport = isKeibaArenaPublicPath(p);
  const geneAnalysisViewport = p.includes('/gene-analysis');
  const reservationViewport = p === '/reservations' || p.startsWith('/reservations/');
  const sampleManagerViewport = isSampleManagerPathname(p);
  const adminViewport = p === '/admin' || p.startsWith('/admin/');
  /** 共有URL: フルナビはカレンダー作業を圧迫するので最小バーにする */
  const reservationShareViewport =
    p === '/reservations/share' || p.startsWith('/reservations/share/');
  const flexViewport = keisukeFlexViewport || keibaArenaViewport || geneAnalysisViewport;

  useEffect(() => {
    if (!reservationShareViewport) {
      setShareBoardTitle('');
      return;
    }
    setShareBoardTitle(getRsShareBoardTitle());
    return subscribeRsShareBoardTitle(() => setShareBoardTitle(getRsShareBoardTitle()));
  }, [reservationShareViewport]);
  const keisukeDashboardRoot =
    p === '/keisuke' ||
    SUPPORTED_LOCALES.some((loc) => loc !== 'ja' && p === `/${loc}/keisuke`);

  /** 計助トップ（/keisuke のみ）: main をスクロールさせないと縦境界ドラッグが効かない。討議等サブパスは従来どおり auto。
   * 競馬アリーナは情報量が多いためページ縦スクロールを許可（1画面固定は廃止）。 */
  const mainFlexStyle: CSSProperties | undefined = flexViewport
    ? {
        flex: '1 1 auto',
        minHeight: 0,
        overflow:
          keibaArenaViewport
            ? 'auto'
            : keisukeDashboardRoot || geneAnalysisViewport
              ? 'hidden'
              : 'auto',
        display: 'flex',
        flexDirection: 'column',
      }
    : undefined;

  const layoutChrome = (
    <>
      {reservationShareViewport ? (
        <header className="layout-reservation-share-bar flex-shrink-0">
          <Container
            fluid="xl"
            className="d-flex align-items-center justify-content-between gap-2 py-0"
          >
            <Link
              to={user ? '/dashboard' : '/'}
              className="layout-reservation-share-titles text-decoration-none min-w-0"
              title={
                shareBoardTitle
                  ? `${t('navigation.system_name', { defaultValue: 'Kensuke' })} · ${shareBoardTitle}`
                  : t('navigation.system_name', { defaultValue: 'Kensuke' })
              }
            >
              <span className="layout-reservation-share-brand">
                {t('navigation.system_name', { defaultValue: 'Kensuke' })}
              </span>
              {shareBoardTitle ? (
                <span className="layout-reservation-share-board">{shareBoardTitle}</span>
              ) : null}
            </Link>
            <div className="d-flex align-items-center gap-2 flex-shrink-0">
              <LanguageSwitcher compact />
              {!user ? (
                <Link
                  to="/login"
                  className="layout-reservation-share-login text-decoration-none"
                >
                  {t('navigation.login', { defaultValue: 'Login' })}
                </Link>
              ) : (
                <Dropdown align="end" className="layout-reservation-share-user">
                  <Dropdown.Toggle
                    variant="link"
                    size="sm"
                    id="reservation-share-user-nav"
                    className="text-decoration-none"
                    aria-label={t('navigation.user', { defaultValue: 'ユーザー' })}
                  >
                    {formatUserName(user.firstName, user.lastName, i18n.language) ||
                      user.firstName ||
                      user.lastName ||
                      user.email ||
                      user.username ||
                      t('navigation.user', { defaultValue: 'ユーザー' })}
                  </Dropdown.Toggle>
                  <Dropdown.Menu variant="dark" align="end">
                    <Dropdown.Item as={Link} to="/reservations">
                      {t('reservation.board.backToList', { defaultValue: '予約表一覧' })}
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/profile">
                      {t('navigation.profile', { defaultValue: 'プロフィール' })}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleLogout}>
                      {t('navigation.logout', { defaultValue: 'ログアウト' })}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </div>
          </Container>
        </header>
      ) : (
      <Navbar
        bg="dark"
        variant="dark"
        expand="xl"
        expanded={navExpanded}
        onToggle={setNavExpanded}
        className={`flex-shrink-0 layout-global-nav${
          reservationViewport || sampleManagerViewport ? ' layout-global-nav--dense' : ''
        }`}
      >
        <Container fluid="xl">
          <Navbar.Brand as={Link} to={user ? '/dashboard' : '/'}>
            {t('navigation.system_name', { defaultValue: 'Kensuke' })}
          </Navbar.Brand>
          {sampleManagerViewport && !isGuestOnPublicDemo ? (
            <SampleManagerSectionSelect className="d-md-none sample-manager-section-select" />
          ) : null}
          <Navbar.Toggle
            aria-controls="basic-navbar-nav"
            label={t('common.menu_open', { defaultValue: 'メニューを開く' })}
            title={t('common.menu_open_title', {
              defaultValue: 'サイトメニューを開く',
            })}
          />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto flex-xl-nowrap">
              {!isGuestOnPublicDemo && (
                <>
              {/* ダッシュボード */}
              <Nav.Link
                as={Link}
                to="/dashboard"
                aria-label={t('navigation.dashboard', { defaultValue: 'ダッシュボード' })}
              >
                {t('navigation.dashboard', { defaultValue: 'ダッシュボード' })}
              </Nav.Link>
                </>
              )}

              {geneAnalysisViewport ? (
                <>
                  <Nav.Link
                    as={Link}
                    to="/gene-analysis"
                    aria-current="page"
                    aria-label={t('geneAnalysis.title', { defaultValue: '遺伝子解析' })}
                    onClick={trackFeatureEntrance('gene_analysis')}
                  >
                    {t('navigation.gene_analysis_nav', { defaultValue: '遺伝子解析' })}
                  </Nav.Link>
                  <NavDropdown
                    title={t('navigation.other_systems', { defaultValue: '他の機能' })}
                    id="gene-analysis-other-nav"
                    aria-label={t('navigation.other_systems', { defaultValue: '他の機能' })}
                  >
                    {keisukeNavVisible && (
                      <NavDropdown.Item
                        as={Link}
                        to={keisukePublicPath(i18n.language)}
                        aria-label={t('navigation.keisuke_nav', { defaultValue: '計助' })}
                        title={t('navigation.keisuke', { defaultValue: '計助・統計解析' })}
                        onClick={trackFeatureEntrance('keisuke')}
                      >
                        {t('navigation.keisuke_nav', { defaultValue: '計助' })}
                      </NavDropdown.Item>
                    )}
                    {laboSukeNavVisible && (
                      <NavDropdown.Item
                        as={Link}
                        to={laboSukePublicPath(i18n.language)}
                        aria-label={t('navigation.labo_suke_aria')}
                        onClick={trackFeatureEntrance('labo_suke')}
                      >
                        {t('navigation.labo_suke')}
                      </NavDropdown.Item>
                    )}
                    {tppNavVisible && (
                      <NavDropdown.Item
                        as={Link}
                        to="/transit-pass-pool"
                        title={t('navigation.transit_pass_pool_guest_aria')}
                        onClick={trackFeatureEntrance('transit_pass')}
                      >
                        {t('navigation.transit_pass_nav', { defaultValue: '交通IC' })}
                      </NavDropdown.Item>
                    )}
                    <NavDropdown.Divider />
                    <NavDropdown.Item
                      as={Link}
                      to="/gaisuke"
                      aria-label={t('navigation.gaisuke', { defaultValue: 'AIトレードアリーナ' })}
                      onClick={trackFeatureEntrance('gaisuke')}
                    >
                      {t('navigation.gaisuke', { defaultValue: 'AIトレードアリーナ' })}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to={gaisukeFxPublicPath(i18n.language)}
                      aria-label={t('navigation.gaisuke_fx', {
                        defaultValue: 'AIトレードアリーナ（FXドル円編）',
                      })}
                      onClick={trackFeatureEntrance('gaisuke_fx')}
                    >
                      {t('navigation.gaisuke_fx', {
                        defaultValue: 'AIトレードアリーナ（FXドル円編）',
                      })}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to={keibaArenaPublicPath(i18n.language)}
                      aria-label={t('navigation.keiba_arena', { defaultValue: 'AI競馬アリーナ' })}
                      onClick={trackFeatureEntrance('keiba_arena')}
                    >
                      {t('navigation.keiba_arena', { defaultValue: 'AI競馬アリーナ' })}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to={banquetPublicPath(i18n.language)}
                      aria-label={t('navigation.banquet', { defaultValue: '熱狂の宴' })}
                      onClick={trackFeatureEntrance('banquet')}
                    >
                      {t('navigation.banquet', { defaultValue: '熱狂の宴' })}
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      as={Link}
                      to={econReportPublicPath(i18n.language)}
                      aria-label={t('navigation.econ_report', { defaultValue: 'AI投資情報' })}
                      onClick={trackFeatureEntrance('econ_report')}
                    >
                      {t('navigation.econ_report', { defaultValue: 'AI投資情報' })}
                    </NavDropdown.Item>
                    {!isGuestOnPublicDemo && (
                      <>
                        <NavDropdown.Divider />
                        <NavDropdown.Item
                          as={Link}
                          to="/samples"
                          aria-label={t('navigation.sample_list', { defaultValue: 'サンプル一覧' })}
                          onClick={trackFeatureEntrance('sample')}
                        >
                          {t('navigation.menu_sample', { defaultValue: 'サンプル管理' })}
                        </NavDropdown.Item>
                        <NavDropdown.Item
                          as={Link}
                          to="/reservations"
                          aria-label={t('navigation.reservation_boards', { defaultValue: '予約表一覧' })}
                          onClick={trackFeatureEntrance('reservation')}
                        >
                          {t('navigation.menu_reservation', { defaultValue: '予約' })}
                        </NavDropdown.Item>
                        {isAdminUser(user) && (
                          <NavDropdown.Item
                            as={Link}
                            to="/admin"
                            aria-label={t('admin.title', { defaultValue: '管理者設定' })}
                          >
                            {t('navigation.menu_admin', { defaultValue: '管理' })}
                          </NavDropdown.Item>
                        )}
                      </>
                    )}
                  </NavDropdown>
                </>
              ) : (
                <>
              {/* 計助（統計解析）— 公開時はゲストも利用可。ナビは短い表記 */}
              {keisukeNavVisible && (
                <Nav.Link
                  as={Link}
                  to={keisukePublicPath(i18n.language)}
                  aria-label={t('navigation.keisuke_nav', { defaultValue: '計助' })}
                  title={t('navigation.keisuke', { defaultValue: '計助・統計解析' })}
                  onClick={trackFeatureEntrance('keisuke')}
                >
                  {t('navigation.keisuke_nav', { defaultValue: '計助' })}
                </Nav.Link>
              )}

              {/* ラボ助（研究費・購入記録）— 公開時はゲストも利用可 */}
              {laboSukeNavVisible && (
                <Nav.Link
                  as={Link}
                  to={laboSukePublicPath(i18n.language)}
                  aria-label={t('navigation.labo_suke_aria')}
                  onClick={trackFeatureEntrance('labo_suke')}
                >
                  {t('navigation.labo_suke')}
                </Nav.Link>
              )}

              {/* 交通IC・共用カード — 公開時はゲストも利用可（Zip 正本） */}
              {tppNavVisible && (
                <Nav.Link
                  as={Link}
                  to="/transit-pass-pool"
                  title={t('navigation.transit_pass_pool_guest_aria')}
                  onClick={trackFeatureEntrance('transit_pass')}
                >
                  {t('navigation.transit_pass_nav', { defaultValue: '交通IC' })}
                </Nav.Link>
              )}

              {/* AIコンテンツ（本編／FXドル円編／競馬／宴／投資情報）— 公開閲覧 */}
              <NavDropdown
                title={t('navigation.menu_arena', { defaultValue: 'AIコンテンツ' })}
                id="arena-nav-dropdown"
                aria-label={t('navigation.menu_arena', { defaultValue: 'AIコンテンツ' })}
              >
                <NavDropdown.Item
                  as={Link}
                  to="/gaisuke"
                  aria-label={t('navigation.gaisuke', { defaultValue: 'AIトレードアリーナ' })}
                  onClick={trackFeatureEntrance('gaisuke')}
                >
                  {t('navigation.gaisuke', { defaultValue: 'AIトレードアリーナ' })}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to={gaisukeFxPublicPath(i18n.language)}
                  aria-label={t('navigation.gaisuke_fx', {
                    defaultValue: 'AIトレードアリーナ（FXドル円編）',
                  })}
                  onClick={trackFeatureEntrance('gaisuke_fx')}
                >
                  {t('navigation.gaisuke_fx', {
                    defaultValue: 'AIトレードアリーナ（FXドル円編）',
                  })}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to={keibaArenaPublicPath(i18n.language)}
                  aria-label={t('navigation.keiba_arena', { defaultValue: 'AI競馬アリーナ' })}
                  onClick={trackFeatureEntrance('keiba_arena')}
                >
                  {t('navigation.keiba_arena', { defaultValue: 'AI競馬アリーナ' })}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to={banquetPublicPath(i18n.language)}
                  aria-label={t('navigation.banquet', { defaultValue: '熱狂の宴' })}
                  onClick={trackFeatureEntrance('banquet')}
                >
                  {t('navigation.banquet', { defaultValue: '熱狂の宴' })}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to={econReportPublicPath(i18n.language)}
                  aria-label={t('navigation.econ_report', { defaultValue: 'AI投資情報' })}
                  onClick={trackFeatureEntrance('econ_report')}
                >
                  {t('navigation.econ_report', { defaultValue: 'AI投資情報' })}
                </NavDropdown.Item>
              </NavDropdown>

              {!isGuestOnPublicDemo && (
                <>
              {/* サンプル管理 */}
              <NavDropdown
                title={t('navigation.menu_sample', { defaultValue: 'サンプル管理' })}
                id="sample-nav-dropdown"
                aria-label={t('navigation.menu_sample', { defaultValue: 'サンプル管理' })}
              >
                {SAMPLE_MANAGER_NAV_ITEMS.map((item) => (
                  <NavDropdown.Item
                    key={item.path}
                    as={Link}
                    to={item.path}
                    aria-label={t(item.labelKey, { defaultValue: item.defaultLabel })}
                    onClick={trackFeatureEntrance('sample')}
                  >
                    {t(item.labelKey, { defaultValue: item.defaultLabel })}
                  </NavDropdown.Item>
                ))}
              </NavDropdown>

              {/* 予約 */}
              <NavDropdown
                title={t('navigation.menu_reservation', { defaultValue: '予約' })}
                id="reservation-nav-dropdown"
                aria-label={t('navigation.menu_reservation', { defaultValue: '予約' })}
              >
                <NavDropdown.Item
                  as={Link}
                  to="/reservations"
                  aria-label={t('navigation.reservation_boards', { defaultValue: '予約表一覧' })}
                  onClick={trackFeatureEntrance('reservation')}
                >
                  {t('navigation.reservation_boards', { defaultValue: '予約表一覧' })}
                </NavDropdown.Item>
              </NavDropdown>

              {/* 管理（管理者のみ） */}
              {isAdminUser(user) && (
                <NavDropdown
                  title={t('navigation.menu_admin', { defaultValue: '管理' })}
                  id="admin-nav-dropdown"
                  aria-label={t('navigation.menu_admin', { defaultValue: '管理' })}
                >
                  <NavDropdown.Item as={Link} to="/users" aria-label={t('users.title')}>
                    {t('users.title')}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/admin"
                    aria-label={t('admin.title', { defaultValue: '管理者設定' })}
                  >
                    {t('admin.title', { defaultValue: '管理者設定' })}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/admin/insights/defense"
                    aria-label={t('host_security.page_title', { defaultValue: '利用と防御' })}
                  >
                    {t('host_security.page_title', { defaultValue: '利用と防御' })}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/admin/database-backup"
                    aria-label={t('navigation.database_backup', { defaultValue: 'DBバックアップ・リストア' })}
                  >
                    {t('navigation.database_backup', { defaultValue: 'DBバックアップ・リストア' })}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/admin/announcements"
                    aria-label={t('navigation.admin_announcements', { defaultValue: 'お知らせ管理' })}
                  >
                    {t('navigation.admin_announcements', { defaultValue: 'お知らせ管理' })}
                  </NavDropdown.Item>
                </NavDropdown>
              )}

                </>
              )}
                </>
              )}

              {/* サポート */}
              <NavDropdown
                title={t('navigation.menu_support', { defaultValue: 'サポート' })}
                id="support-nav-dropdown"
                aria-label={t('navigation.menu_support', { defaultValue: 'サポート' })}
              >
                <NavDropdown.Item
                  as={Link}
                  to="/help"
                  aria-label={t('common.help', { defaultValue: 'ヘルプ' })}
                >
                  {t('common.help', { defaultValue: 'ヘルプ' })}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to="/contact"
                  aria-label={t('navigation.contact_private', { defaultValue: 'お問い合わせ（個別）' })}
                >
                  {t('navigation.contact_private', { defaultValue: 'お問い合わせ（個別）' })}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={Link}
                  to="/announcements"
                  aria-label={t('navigation.announcements', { defaultValue: 'お知らせ' })}
                >
                  {t('navigation.announcements', { defaultValue: 'お知らせ' })}
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
            <Nav>
              <LanguageSwitcher />
              {!user && (
                <Nav.Link
                  as={Link}
                  to="/login"
                  aria-label={t('navigation.login', { defaultValue: 'Login' })}
                >
                  {t('navigation.login', { defaultValue: 'Login' })}
                </Nav.Link>
              )}
              {user && (
                <NavDropdown
                  title={
                    formatUserName(user.firstName, user.lastName, i18n.language) ||
                    user.firstName ||
                    user.lastName ||
                    user.email ||
                    user.username ||
                    t('navigation.user', { defaultValue: 'ユーザー' })
                  }
                  id="user-nav-dropdown"
                  className="layout-global-nav-user"
                  aria-label={t('navigation.user', { defaultValue: 'ユーザー' })}
                >
                  <NavDropdown.Item
                    as={Link}
                    to="/profile"
                    aria-label={t('navigation.profile', { defaultValue: 'プロフィール' })}
                  >
                    {t('navigation.profile', { defaultValue: 'プロフィール' })}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item
                    as={Link}
                    to="/help"
                    aria-label={t('common.help', { defaultValue: 'ヘルプ' })}
                  >
                    {t('common.help', { defaultValue: 'ヘルプ' })}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/privacy"
                    aria-label={t('common.privacy_policy', {
                      defaultValue: 'プライバシーポリシー',
                    })}
                  >
                    {t('common.privacy_policy', { defaultValue: 'プライバシーポリシー' })}
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    as={Link}
                    to="/terms"
                    aria-label={t('common.terms', { defaultValue: '利用規約' })}
                  >
                    {t('common.terms', { defaultValue: '利用規約' })}
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item
                    onClick={handleLogout}
                    aria-label={t('navigation.logout', { defaultValue: 'ログアウト' })}
                  >
                    {t('navigation.logout', { defaultValue: 'ログアウト' })}
                  </NavDropdown.Item>
                </NavDropdown>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      )}
      <main
        id="main-content"
        role="main"
        className={
          keibaArenaViewport
            ? 'layout-main-keiba'
            : keisukeFlexViewport
              ? 'layout-main-keisuke'
              : geneAnalysisViewport
                ? 'layout-main-gene-analysis'
                : sampleManagerViewport
                  ? 'layout-main-sample'
                  : adminViewport
                    ? 'layout-main-admin'
                    : undefined
        }
        style={mainFlexStyle}
      >
        {children}
      </main>
      {/* 共有予約表は固定登録バーと競合しやすいのでフッターを出さない */}
      {!reservationShareViewport ? (
        <Footer
          noTopMargin={flexViewport}
          compact={
            keibaArenaViewport ||
            keisukeFlexViewport ||
            geneAnalysisViewport ||
            reservationViewport ||
            sampleManagerViewport
          }
        />
      ) : null}
    </>
  );

  return (
    <>
      <a href="#main-content" className="skip-link">
        {t('common.skip_to_main_content', { defaultValue: 'メインコンテンツにスキップ' })}
      </a>
      {flexViewport ? (
        <div className="d-flex flex-column min-vh-100">{layoutChrome}</div>
      ) : (
        layoutChrome
      )}
    </>
  );
}
